import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CaseListItem, CaseInformationService } from 'src/app/services/case-information.service';

@Component({
    selector: 'app-case-list',
    templateUrl: './case-list.component.html',
    styleUrls: ['./case-list.component.scss'],
    standalone: false
})
export class CaseListComponent implements OnInit {

    relId: string = '';
    clientName: string = '';
    clientType: string = '';
    casesShared: CaseListItem[] = [];
    loading: boolean = true;

    searchTerm: string = '';
    sortOrder: 'latest' | 'oldest' = 'latest';


    isSortMenuOpen = false;
    readonly sortOptions: { value: 'latest' | 'oldest'; label: string }[] = [
        { value: 'latest', label: 'Latest first' },
        { value: 'oldest', label: 'Oldest first' }
    ];

    @ViewChild('sortFieldRef') private sortFieldRef?: ElementRef<HTMLElement>;

    clientAvatarUrl?: string;

    private navigationInProgress = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private caseInformationService: CaseInformationService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        this.relId = this.route.snapshot.paramMap.get('relId') || '';

        const state: any = history.state;
        this.clientName = state?.clientName || '';
        this.clientType = state?.clientType || '';
        this.clientAvatarUrl = state?.clientAvatarUrl || state?.avatarUrl || state?.clientImage || undefined;

        this.loadCases();
    }


    private loadCases(): void {
        if (!this.relId) {
            this.casesShared = [];
            this.loading = false;
            return;
        }

        this.loading = true;
        this.caseInformationService.getSharedCaseList(this.relId).subscribe({
            next: (list) => {
                this.casesShared = list;
                console.log('Fetched shared cases:', list);

              
                this.loading = false;

                if (!this.clientAvatarUrl) {
                    this.clientAvatarUrl = list.find(c => !!c.profilePic)?.profilePic;
                }
            },
            error: () => {
                this.casesShared = [];
                this.loading = false;
                this.toastr.error('Unable to load cases shared. Please try again.');
            }
        });
    }

    get casesCountLabel(): string {
        const count = this.casesShared?.length || 0;
        return count === 1 ? '1 Case' : `${count} Cases`;
    }

    get clientInitials(): string {
        const parts = (this.clientName || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) { return ''; }
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
    }

    get filteredCases(): CaseListItem[] {
        const term = this.searchTerm.trim().toLowerCase();
        let list = this.casesShared;

        if (term) {
            list = list.filter(c => {
                const title = (c.issueTitle || '').toLowerCase();
                const types = (c.issueType || []).join(' ').toLowerCase();
                return title.includes(term) || types.includes(term);
            });
        }

        return [...list].sort((a, b) => {
            const dateA = new Date(a.sharedOn).getTime();
            const dateB = new Date(b.sharedOn).getTime();
            return this.sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
        });
    }

    onSearchChange(value: string): void {
        this.searchTerm = value;
    }

    get sortLabel(): string {
        return this.sortOptions.find(o => o.value === this.sortOrder)?.label || 'Latest first';
    }

    toggleSortMenu(): void {
        this.isSortMenuOpen = !this.isSortMenuOpen;
    }

    closeSortMenu(): void {
        this.isSortMenuOpen = false;
    }

    selectSort(value: 'latest' | 'oldest'): void {
        this.sortOrder = value;
        this.isSortMenuOpen = false;
    }


    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.isSortMenuOpen || !this.sortFieldRef) {
            return;
        }
        if (!this.sortFieldRef.nativeElement.contains(event.target as Node)) {
            this.isSortMenuOpen = false;
        }
    }

    viewCase(caseInfo: CaseListItem): void {
        if (this.navigationInProgress) {
            return;
        }
        this.navigationInProgress = true;

        const preReadId = caseInfo.preReadId;
        this.router.navigate(
            ['/relationship/view/individuals', this.relId, 'cases', preReadId],
            {
                state: {
                    clientName: this.clientName,
                    clientType: this.clientType
                }
            }
        ).finally(() => {
            this.navigationInProgress = false;
        });
    }

    documentCount(caseInfo: CaseListItem): string {
        return caseInfo.documentsCount === undefined ? '—' : String(caseInfo.documentsCount);
    }

    backToRelationships(): void {
        this.router.navigate(['/relationship/view/individuals']);
    }
}